/**
 * Org-unit tree ranks (docs/decisions/2026-09-11-garments-org-hierarchy.md §2): a parent's rank
 * must be strictly lower than its child's; DEPARTMENT (rank 0) may never have a parent, but every
 * other rank may still be a root -- a FLOOR/SECTION/LINE can hang directly off the institution.
 */
export type DepartmentUnitType = 'DEPARTMENT' | 'FLOOR' | 'SECTION' | 'LINE';

export const DEPARTMENT_UNIT_TYPES: DepartmentUnitType[] = ['DEPARTMENT', 'FLOOR', 'SECTION', 'LINE'];

export const DEPARTMENT_UNIT_TYPE_RANK: Record<DepartmentUnitType, number> = {
    DEPARTMENT: 0,
    FLOOR: 1,
    SECTION: 2,
    LINE: 3,
};

export const DEPARTMENT_UNIT_TYPE_LABEL: Record<DepartmentUnitType, string> = {
    DEPARTMENT: 'Department',
    FLOOR: 'Floor',
    SECTION: 'Section',
    LINE: 'Line',
};

export interface Department {
    id: string;
    name: string;
    description?: string;
    institutionId: string;
    unitType: DepartmentUnitType;
    parentId: string | null;
    code: string | null;
    sortOrder: number;
    /** B4-H: optional cost-centre code (unique per institution); matched first by the journal import. */
    costCentreCode?: string | null;
}

export interface DepartmentResponse {
    content: Department[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

export interface CreateDepartmentDto {
    name: string;
    description?: string;
    institutionId?: string;
    unitType?: DepartmentUnitType;
    parentId?: string | null;
    code?: string | null;
    sortOrder?: number;
    /** B4-H: blank clears the cost-centre code on update. */
    costCentreCode?: string | null;
}

export interface DepartmentUser {
    id: string;
    departmentId: string;
    userId: string;
    isDepartmentAdmin: boolean;
    username?: string;
    fullName?: string | null;
}

/** `GET /department/institution/{institutionId}/tree` node (nested, roots sorted by sortOrder/name). */
export interface DepartmentTreeNodeDTO {
    id: string;
    name: string;
    code: string | null;
    unitType: DepartmentUnitType;
    sortOrder: number;
    children: DepartmentTreeNodeDTO[];
}
